import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  LinearProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Grid,
  Box,
} from '@mui/material';
import { GroupAdd } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../services/api';

type MonthRow = {
  month: string;
  newCustomers: number;
  newOrders: number;
  newTotal: number;
  returningCustomers: number;
  returningOrders: number;
  returningTotal: number;
  unassignedOrders: number;
  unassignedTotal: number;
};

const RANGES = [6, 12, 24];
const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const NEW_COLOR = '#2E7D32';
const RETURNING_COLOR = '#1565C0';

const formatNumber = (val: number) => (val ?? 0).toLocaleString('es-AR', { maximumFractionDigits: 0 });
const formatCurrency = (val: number) => `$${formatNumber(val)}`;
const formatAxisM = (value: number) =>
  `$${((Number(value) || 0) / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M`;
const monthLabel = (key: string) => {
  const [y, m] = key.split('-');
  return `${MONTH_NAMES[Number(m) - 1]} ${y.slice(2)}`;
};
const pct = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 100) : 0);
const salesLabel = (n: number) => `${formatNumber(n)} ${n === 1 ? 'venta' : 'ventas'}`;

const NewVsReturningReport: React.FC = () => {
  const [months, setMonths] = useState(12);
  const [items, setItems] = useState<MonthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getNewVsReturning(months);
      setItems(result.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const current = items.length > 0 ? items[items.length - 1] : null;
  const currentAssignedTotal = current ? current.newTotal + current.returningTotal : 0;
  const chartData = items.map(r => ({ month: monthLabel(r.month), Nuevos: r.newTotal, Recurrentes: r.returningTotal }));
  // Más reciente arriba en la tabla.
  const tableRows = [...items].reverse();

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupAdd color="primary" />
            <Typography variant="h6" fontWeight={600}>Clientes Nuevos vs Recurrentes</Typography>
          </Stack>
          <ToggleButtonGroup size="small" exclusive value={months} onChange={(_, v) => v && setMonths(v)}>
            {RANGES.map(r => (
              <ToggleButton key={r} value={r}>{r} meses</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Un cliente es nuevo en el mes de su primera compra y recurrente en los meses siguientes. Las ventas sin cliente asignado se muestran aparte.
        </Typography>

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {current && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="caption" color="text.secondary">Clientes nuevos — {monthLabel(current.month)}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: NEW_COLOR }}>{formatNumber(current.newCustomers)}</Typography>
                <Typography variant="caption" color="text.secondary">{salesLabel(current.newOrders)} · {formatCurrency(current.newTotal)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="caption" color="text.secondary">Clientes recurrentes — {monthLabel(current.month)}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: RETURNING_COLOR }}>{formatNumber(current.returningCustomers)}</Typography>
                <Typography variant="caption" color="text.secondary">{salesLabel(current.returningOrders)} · {formatCurrency(current.returningTotal)}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="caption" color="text.secondary">% del monto — {monthLabel(current.month)}</Typography>
                <Typography variant="h5" fontWeight={700}>
                  <Box component="span" sx={{ color: NEW_COLOR }}>{pct(current.newTotal, currentAssignedTotal)}%</Box>
                  {' / '}
                  <Box component="span" sx={{ color: RETURNING_COLOR }}>{pct(current.returningTotal, currentAssignedTotal)}%</Box>
                </Typography>
                <Typography variant="caption" color="text.secondary">nuevos / recurrentes</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>
        )}

        {items.length > 0 && (
          <Box sx={{ height: 320, mb: 3 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={formatAxisM} width={70} />
                <RechartsTooltip formatter={(v) => formatCurrency(Number(v))} />
                <Legend />
                <Bar dataKey="Nuevos" stackId="a" fill={NEW_COLOR} />
                <Bar dataKey="Recurrentes" stackId="a" fill={RETURNING_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {items.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mes</TableCell>
                  <TableCell align="right">Clientes nuevos</TableCell>
                  <TableCell align="right">Ventas nuevos</TableCell>
                  <TableCell align="right">Monto nuevos</TableCell>
                  <TableCell align="right">Clientes recurrentes</TableCell>
                  <TableCell align="right">Ventas recurrentes</TableCell>
                  <TableCell align="right">Monto recurrentes</TableCell>
                  <TableCell align="right">Ventas sin cliente</TableCell>
                  <TableCell align="right">Monto sin cliente</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map(r => (
                  <TableRow key={r.month}>
                    <TableCell>{monthLabel(r.month)}</TableCell>
                    <TableCell align="right">{formatNumber(r.newCustomers)}</TableCell>
                    <TableCell align="right">{formatNumber(r.newOrders)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.newTotal)}</TableCell>
                    <TableCell align="right">{formatNumber(r.returningCustomers)}</TableCell>
                    <TableCell align="right">{formatNumber(r.returningOrders)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.returningTotal)}</TableCell>
                    <TableCell align="right">{formatNumber(r.unassignedOrders)}</TableCell>
                    <TableCell align="right">{formatCurrency(r.unassignedTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default NewVsReturningReport;
